import React, { useState, useMemo, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { format, parse } from 'date-fns'
import { IProductsWithVarietyItem } from '../../../../../../../modules/API/Defaults/models/compliteProducts'
import { IPackagesItem } from '../../../../../../../modules/API/Defaults/models/packages'
import { IPalletTypeItem } from '../../../../../../../modules/API/Defaults/models/pallets'
import { IDetailsSelected } from '../../CreateKnisa'
import { IEntriesKnisotItem } from '../../../../../../../modules/API/Knisot/models/entrie'
import { IEntriePutData } from '../../../../../../../modules/API/Market/models/putRequestInterface'
import { ISizeItem } from '../../../../../../../modules/API/Products/models/size'
import { IWarhouseListItem } from '../../../../../../../modules/API/Defaults/models/warhouseList'
import { IRecommendationItem } from '../../../../../../../modules/API/Recommendation/models/recomendationItem'
import { handleNotifications } from '../../../../../../../generic/utils/handleNotifications'
import selectOnFocus from '../../../../../../../modules/autoselect'
import { useKnisaConfig } from '../../config'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../../../../../store/reducers/rootReducer'
import { SET_IS_OPEN } from '../../../../../../../../store/reducers/printer'
import { EXTERNAL } from '../../../../../../../modules/API/External/external'
import { vibrationFunc } from '../../../../../../../modules/vibrationModule'
import useDataFetcher from '../../../hooks/useFetchData'
import { Product } from '../../../../../../../modules/API/Products/products'
import useWeightCalculation from '../../../hooks/useWeightCalculation'
import { useTranslation } from 'react-i18next'

import ProductSelector from '../../../../../../../generic/ui/Selectors/ProductSelector'
import WarehouseLocationSelector from '../../../../../../../generic/ui/Selectors/WarehouseLocationSelector'
import SizeSelector from '../../../../../../../generic/ui/Selectors/SizeSelector'
import PackageSelector from '../../../../../../../generic/ui/Selectors/PackageSelector'
import PalletTypeSelector from '../../../../../../../generic/ui/Selectors/PalletTypeSelector'
import WarehouseSelector from '../../../../../../../generic/ui/Selectors/WarehouseSelector'
import IconTextField from '../../../../../../../generic/ui/Input/IconTextField'
import { FieldHint } from '../../../../../../../generic/ui/Input/FieldHint'
import LoadingDotsAnimated from '../LoadingDotsAnimated'
import Checkbox from '../Checkbox'
import DateField from '../../../../orders/Components/DateField'
import { AccentButton } from '../../../../../../../generic/ui/Button/AccentButton'
import {
  FormShell,
  FormGrid,
  FormField,
  FormFooter,
} from '../../../../../../../generic/ui/FormLayout'
import CoinIcon from '../../../../../../../generic/icons/IconLib/CoinIcon'
import HashIcon from '../../../../../../../generic/icons/IconLib/HashIcon'
import ScaleIcon from '../../../../../../../generic/icons/IconLib/ScaleIcon'
import TagIcon from '../../../../../../../generic/icons/IconLib/TagIcon'
import PackageBoxIcon from '../../../../../../../generic/icons/IconLib/PackageBoxIcon'
import { useWarehouseLocations, findLocationByCode } from '../../../../../../../generic/hooks/useWarehouseLocations'
import { IWarehouseLocation } from '../../../../../../../modules/API/Warehouse/models/locations'
import { useBrodMigdal } from '../../../../../../../generic/hooks/useBrodMigdal'
import { parseGroupIDs, filterByGroupIDs } from '../Helpers/filterByGroupID'
import useExternalPalletCheck from '../../services/hooks/useExternalPalletCheck'

type Props = {
  productsWithVariety: IProductsWithVarietyItem[]
  packageList: IPackagesItem[]
  cashedDetails: IEntriesKnisotItem | null
  sizeList: ISizeItem[]
  handleCreateDetails: (data: IDetailsSelected, isNeedToCreatePallet: boolean, addictional?: { multyplier: number, isCopyWithWeigh: boolean, isEdit: boolean}) => void
  handleChangeDetails: (data: IEntriePutData) => void,
  handleControlModal: () => void,
  warehouseList: IWarhouseListItem[]
  tranzitID: number
  growerProductList?: IRecommendationItem[] | null
  growerID?: number
}

const ModalKnisaAddProduct = ({
  productsWithVariety, packageList,
   cashedDetails, tranzitID,
  handleCreateDetails, handleChangeDetails,
  sizeList, handleControlModal, warehouseList,
  growerProductList, growerID,
}: Props) => {
  const [t] = useTranslation(['knisa', 'buttons'])
  const { locations } = useWarehouseLocations()
  const { getBrodMigdal } = useBrodMigdal()
  const dispatch = useDispatch()

  const scales = useSelector((state: RootState) => state.printerReducer.scales)
  const config = useKnisaConfig()
  // Паллета на строку уже создана — пересоздать её отсюда нельзя,
  // поэтому чекбокс показываем, но блокируем.
  const isPalletAlreadyCreated =
    !!cashedDetails && (cashedDetails.Pallet_Num !== 0 || cashedDetails.PalletDetailsCount !== 0)
  const defaultPackage = cashedDetails?.TypePack || config.create.details.package[0].value as number
  const defaultPallet = cashedDetails?.TypePallet || config.create.details.pallet[0].value as number
  const defaultProductID = cashedDetails?.ProductID || config.create.details.productID[0].value as number
  const defaultVarietyID = cashedDetails?.VarietyID || config.create.details.varietyID[0].value as number
  const defaultTranzit = cashedDetails?.Tranzit || tranzitID
  const defaultSizeID = cashedDetails?.SizeNum ? sizeList?.find((item) => item.Code === cashedDetails.SizeNum) : null
  const hasExistingExpiry = !!cashedDetails?.ExpirationDate && cashedDetails.ExpirationDate !== '00000000' && cashedDetails.ExpirationDate?.trim() !== '0'
  const defaultExpirationDate = hasExistingExpiry ? parse(cashedDetails!.ExpirationDate as string, "yyyyMMdd", new Date()) : new Date()

  const defaultIsNeedToCreatePallet =
    !cashedDetails ? config.create.details.toPallet[0].value as boolean : cashedDetails?.ToPallet || false

  const [productQty, setProductQty] = useState<number>(cashedDetails?.AmountPack || 0)
  const [balanceAmount, setBalanceAmount] = useState<number>(cashedDetails?.BalanceAmount ?? 0)
  const [productID, setProductID] = useState<number>(defaultProductID || 0)
  const [varietyID, setVarietyID] = useState<number>(defaultVarietyID || 0)
  const [sizeID, setSizeID] = useState<ISizeItem | null>(defaultSizeID || null)
  const [price, setPrice] = useState<number>(cashedDetails?.PriceWeight || 0)
  const [weight, setWeight] = useState<number>(cashedDetails?.Gross || 0)
  const [remark, setRemark] = useState<string>(cashedDetails?.Remark || '')
  const [palletID, setPalletID] = useState<number>(defaultPallet || 0)
  const [prevPalletID, setPrevPalletID] = useState<number>(cashedDetails?.TypePallet || 0)
  const [packageID, setPackageID] = useState<number>(defaultPackage || 0)
  const [location, setLocation] = useState<string>(cashedDetails?.Locating?.trim() || '')
  const [multiplier, setMultiplier] = useState<number>(0)
  const [isCopyWeight, setIsCopyWeight] = useState<boolean>(config.create.details.multuplier[0].value as boolean)
  const [externalPalletNum, setExternalPalletNum] = useState<number>(cashedDetails?.ExternalPallet || 0)
  // Только предупреждение — сохранение не блокируем
  const { isExist: isExternalPalletExist } = useExternalPalletCheck({
    externalPallet: externalPalletNum,
    growerID: growerID || 0,
    ownExternalPallet: cashedDetails?.ExternalPallet || 0,
  })
  const [isLoadScales, setIsLoadScales] = useState<boolean>(false)
  const [selectedTranzit, setSelectedTranzit] = useState<number>(defaultTranzit || 0)
  const [isNeedToCreatePallet, setIsNeedToCreatePallet] = useState<boolean>(defaultIsNeedToCreatePallet)
  const [expiryDate, setExpiryDate] = useState<Date>(defaultExpirationDate || new Date())
  const [isExpiryDateActive, setIsExpiryDateActive] = useState<boolean>(cashedDetails ? hasExistingExpiry : false)
  const {
    calculationWeightWithBom,
    calculationWeighNetto,
    isLoadBom,
    palletList
  } = useWeightCalculation({
    product: {
      ProductID: productID,
      VarietyID: varietyID,
      PackID: packageID,
      SizeID: sizeID?.Code || 0
    }
  })

  const withoutPalletID = useMemo(() => {
    return palletList.find((item) => item.Real_Pallete === 0)?.Pallete_Type
  }, [palletList])

  const [palletQty, setPalletQty] = useState<number>(defaultPallet !== withoutPalletID ? cashedDetails?.AmountPallets || 1 : 0)


  const {
    data: sizeListByProduct
  } = useDataFetcher<ISizeItem[]>(
    Product.getSizesByProduct,
    productID.toString()
  )


  const isActiveSave = useMemo(() => {
    return productQty > 0 && productID !== 0 &&
      (config.create.details.weight[0].value ? weight > 0 : true) &&
      palletID !== 0 && packageID !== 0
  }, [
    productQty,
    config,
    productID,
    weight,
    palletID,
    packageID
  ])

  const fetchScale = async () => {
    if(!scales || scales === 0 ) {
      handleControlModal()
      dispatch({type: SET_IS_OPEN, payload: true})
      return
    }
    try {
      setIsLoadScales(true)
      const res = await EXTERNAL.getWeightFromScales(String(scales))
      if(!Number.isNaN(res.Weight)) {
        setWeight(res.Weight)
        vibrationFunc()
      }

    } catch (err) {

    } finally {
      setIsLoadScales(false)
    }
  }


  const handleChangePalletType = useCallback((id: number) => {
    if (id === withoutPalletID) {
      setPalletQty(0)
    } else if (id !== 0 && prevPalletID === withoutPalletID) {
      setPalletQty(1)
    }
    setPalletID(id)
    if(id !== 0) {
      setPrevPalletID(id)
    }
  }, [withoutPalletID, prevPalletID])

  const handleProductChange = (item: IProductsWithVarietyItem | null) => {
    setProductID(item?.ProductID ?? 0)
    setVarietyID(item?.VarietyID ?? 0)
    setSizeID(null)
    if (
      item && growerProductList &&
      !growerProductList.some(r => r.ProductID === item.ProductID && r.VarietyID === item.VarietyID)
    ) {
      handleNotifications(t('knisa_notification_growerHasNoProduct'), { type: 'warning' })
    }
  }

  const handleCreateDetailsFunc = (isEdit: boolean) => {
    const weightNetto = calculationWeighNetto(weight, palletID, palletQty)
    handleCreateDetails({
      productQty,
      productID,
      varietyID,
      price,
      weight,
      remark,
      palletID,
      packageID,
      externalPalletNum,
      palletQty,
      weightNetto,
      sizeID: sizeID?.Code || 0,
      tranzit: selectedTranzit,
      expiryDate: isExpiryDateActive ? format(expiryDate, 'yyyyMMdd') : '0',
      location: location,
    },
    isNeedToCreatePallet,
    multiplier ? { multyplier: multiplier, isCopyWithWeigh: isCopyWeight, isEdit: isEdit } : undefined)
  }

  const handleChangesDetailsFunc = (isEdit: boolean) => {
    if (!cashedDetails) return
    const weightNetto = calculationWeighNetto(weight, palletID, palletQty)
    handleChangeDetails({
      Details: [{
        DetailNum: cashedDetails.DetailNum,
        ProductID: productID,
        VarietyID: varietyID,
        PriceWeight: price,
        Gross: weight,
        WeightNeto: weightNetto,
        Remark: remark,
        TypePallet: palletID,
        TypePack: packageID,
        AmountPack: productQty,
        ExternalPallet: +externalPalletNum,
        AmountPallets: palletQty,
        SizeNum: sizeID?.Code || 0,
        // Остаток уходит только если его поменяли — иначе сервер хранит своё значение
        ...(balanceAmount !== cashedDetails.BalanceAmount && { BalanceAmount: balanceAmount }),
        Tranzit: selectedTranzit,
        ToPallet: cashedDetails?.ToPallet || isNeedToCreatePallet,
        ExpirationDate: isExpiryDateActive ? format(expiryDate, 'yyyyMMdd') : '0',
        Locating: location,
      }]
    })
    if (multiplier) {
      handleCreateDetailsFunc(isEdit)
    }
  }
  const handleChangeIsNeedToCreatePallet = () => {
    setIsNeedToCreatePallet(!isNeedToCreatePallet)
  }

  useEffect(() => {
    const weightWithBom = calculationWeightWithBom(productQty)
    if(weightWithBom === undefined) return
    setWeight(weightWithBom)
  }, [calculationWeightWithBom, productQty])

  // BM 679: comma-separated product-group ids ('1,3'); empty/invalid = no
  // restriction. Narrows the selector options only; productObj keeps resolving
  // from the full list so an edited row's product outside the groups stays
  // visible and saveable.
  const productGroupIDs = parseGroupIDs(getBrodMigdal({ id: 679 }))
  const productsForSelect = useMemo(
    () => filterByGroupIDs(productsWithVariety, productGroupIDs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productsWithVariety, productGroupIDs.join(',')]
  )

  // Resolve current selected items for the SearchableSelect-based selectors.
  const productObj = useMemo<IProductsWithVarietyItem | null>(() => {
    if (!productID) return null
    return productsWithVariety.find(p => p.ProductID === productID && p.VarietyID === varietyID) ?? null
  }, [productsWithVariety, productID, varietyID])

  const packageObj = useMemo<IPackagesItem | null>(
    () => packageList.find(p => p.PackID === packageID) ?? null,
    [packageList, packageID]
  )

  const palletObj = useMemo<IPalletTypeItem | null>(
    () => palletList.find(p => p.Pallete_Type === palletID) ?? null,
    [palletList, palletID]
  )

  const warehouseObj = useMemo<IWarhouseListItem | null>(
    () => warehouseList.find(w => w.WarehouseID === selectedTranzit) ?? null,
    [warehouseList, selectedTranzit]
  )

  const locationObj = useMemo<IWarehouseLocation | null>(
    () => findLocationByCode(locations, location),
    [locations, location]
  )

  const sizes = sizeListByProduct ?? []
  const showSize = sizes.length > 0 && !!productID

  const isEdit = !!cashedDetails
  const isDeliveryLocked = cashedDetails?.Delivery_Num !== 0
  const handleSubmit = () => {
    if (isEdit) handleChangesDetailsFunc(true)
    else handleCreateDetailsFunc(false)
  }

  return (
    <FormShell>
      <FormGrid>
        <FormField label={t('knisa_create_modal_product_externalEntry')}>
          <IconTextField
            type='number'
            value={externalPalletNum || ''}
            onChange={(e) => setExternalPalletNum(Number(e.target.value))}
            onClick={selectOnFocus}
            leadingIcon={<TagIcon />}
            placeholder='—'
            warning={isExternalPalletExist}
          />
          {isExternalPalletExist && (
            <FieldHint $tone='warning'>{t('knisa_create_modal_product_externalPalletExists')}</FieldHint>
          )}
        </FormField>

        <FormField label={t('knisa_create_modal_product_createPallet')}>
          <ToggleRow>
            <Checkbox
              checked={isNeedToCreatePallet}
              onChange={handleChangeIsNeedToCreatePallet}
              disabled={isPalletAlreadyCreated}
            />
          </ToggleRow>
        </FormField>

        <FormField label={t('knisa_create_modal_product_product')} fullWidth={!showSize}>
          <ProductSelector
            products={productsForSelect}
            value={productObj}
            onSelect={handleProductChange}
          />
        </FormField>

        {showSize && (
          <FormField label={t('knisa_create_modal_product_size')}>
            <SizeSelector
              sizes={sizes}
              value={sizeID}
              onSelect={setSizeID}
            />
          </FormField>
        )}

        <FormField label={t('knisa_create_modal_product_pallet')}>
          <PalletTypeSelector
            palletTypes={palletList}
            value={palletObj}
            onSelect={(p) => handleChangePalletType(p?.Pallete_Type ?? 0)}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_product_qty')}>
          <IconTextField
            type='number'
            value={palletQty}
            onChange={(e) => setPalletQty(Number(e.target.value))}
            onClick={selectOnFocus}
            leadingIcon={<HashIcon />}
            disabled={!palletID}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_product_package')}>
          <PackageSelector
            packages={packageList}
            value={packageObj}
            onSelect={(p) => setPackageID(p?.PackID ?? 0)}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_product_qty')}>
          {isLoadBom ? (
            <LoadingSlot><LoadingDotsAnimated /></LoadingSlot>
          ) : (
            <IconTextField
              type='number'
              value={productQty}
              onChange={(e) => setProductQty(Number(e.target.value))}
              onClick={selectOnFocus}
              leadingIcon={<HashIcon />}
            />
          )}
        </FormField>

        <FormField label={t('knisa_create_modal_product_weight')}>
          <IconTextField
            type='number'
            value={+weight.toFixed(2)}
            onChange={(e) => setWeight(Number(e.target.value))}
            onClick={selectOnFocus}
            onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
            min={-100}
            placeholder={t('knisa_create_modal_product_weight')}
            endAdornment={
              <ScaleBtn type='button' onClick={fetchScale} aria-label={t('knisa_create_modal_product_readScale_aria')}>
                {isLoadScales ? <LoadingDotsAnimated /> : <ScaleIcon />}
              </ScaleBtn>
            }
          />
        </FormField>

        <FormField label={t('knisa_create_modal_product_price')}>
          <IconTextField
            type='number'
            value={price || ''}
            onChange={(e) => setPrice(Number(e.target.value))}
            onClick={selectOnFocus}
            leadingIcon={<CoinIcon />}
            placeholder={t('knisa_create_modal_product_price')}
          />
        </FormField>

        {config.create.details.tranzit[0].value && (
          <FormField label={t('knisa_create_modal_title_warehouse')} fullWidth>
            <WarehouseSelector
              warehouses={warehouseList}
              value={warehouseObj}
              onSelect={(w) => setSelectedTranzit(w?.WarehouseID ?? 0)}
            />
          </FormField>
        )}

        <FormField label={t('knisa_create_modal_title_remark')} fullWidth>
          <RemarkArea
            defaultValue={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder={t('knisa_create_modal_title_remark')}
            rows={2}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_product_copy')}>
          <IconTextField
            type='number'
            value={multiplier || ''}
            onChange={(e) => setMultiplier(Number(e.target.value))}
            onClick={selectOnFocus}
            leadingIcon={<HashIcon />}
            placeholder='—'
          />
        </FormField>

        <FormField label={' '}>
          <ToggleRow>
            <Checkbox
              checked={isCopyWeight}
              onChange={setIsCopyWeight}
              label={t('knisa_create_modal_product_includeWeight')}
            />
          </ToggleRow>
        </FormField>

        <FormField label={t('knisa_create_modal_product_detection')}>
          <WarehouseLocationSelector
            locations={locations ?? []}
            value={locationObj}
            onSelect={(l) => setLocation(l?.CodeLocation.trim() ?? '')}
            placeholder={t('knisa_create_modal_product_detection')}
            allowClear
          />
        </FormField>

        <FormField label={t('knisa_create_modal_product_expiryDate')}>
          <ExpiryRow>
            <Checkbox
              checked={isExpiryDateActive}
              onChange={setIsExpiryDateActive}
            />
            <ExpiryDateSlot>
              <DateField
                selected={expiryDate}
                onChange={(d) => d && setExpiryDate(d)}
                disabled={!isExpiryDateActive}
              />
            </ExpiryDateSlot>
          </ExpiryRow>
        </FormField>

        {isEdit && (
          <FormField label={t('knisa_create_modal_product_balance')} fullWidth>
            <IconTextField
              type='number'
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(Number(e.target.value))}
              onClick={selectOnFocus}
              leadingIcon={<PackageBoxIcon />}
            />
          </FormField>
        )}
      </FormGrid>

      <FormFooter>
        <AccentButton
          type='button'
          onClick={handleSubmit}
          disabled={!isActiveSave || (isEdit && isDeliveryLocked)}
          fullWidth={false}
        >
          {t('buttons:save')}
        </AccentButton>
      </FormFooter>
    </FormShell>
  )
}

export default ModalKnisaAddProduct

const ExpiryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-height: 2.875rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 2.5rem;
  }
`

const ExpiryDateSlot = styled.div`
  flex: 1;
  min-width: 0;
`

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-height: 2.875rem;
  padding: 0 0.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 2.5rem;
  }
`

const LoadingSlot = styled.div`
  min-height: 2.875rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 0.875rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 2.5rem;
  }
`

const ScaleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.icon};
  cursor: pointer;
  border-radius: 0.5rem;
  transition:
    color ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.ink};
    background: ${({ theme }) => theme.colors.accentSoft};
  }
`

const RemarkArea = styled.textarea`
  width: 100%;
  min-height: 4.5rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 0.875rem;
  padding: 0.625rem 0.875rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  outline: none;
  resize: vertical;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`

